import { useState, useEffect, useRef } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { CircularProgress } from "@/components/ui/circular-progress";
import { Input } from "@/components/ui/input";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { MoreHorizontal, Edit2, Trash2, Target } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Mission, Todo, MissionSize } from "@/types/todo";
import { calculateMissionProgress } from "@/utils/missionHelpers";
import MissionCreationModal from "@/components/MissionCreationModal";

interface MissionCardsProps {
  missions: Mission[];
  allTodos: Todo[];
  activeTab: string;
  isMobile?: boolean;
  generalMissionCount: number;
  onCompleteMission: (missionId: string) => void;
  onEditMission: (missionId: string, newName: string) => void;
  onDeleteMission: (missionId: string) => void;
  onCreateMission: (name: string, size: MissionSize) => { id: string; mission: Mission };
}

export default function MissionCards({ missions, allTodos, activeTab, isMobile = false, generalMissionCount, onCompleteMission, onEditMission, onDeleteMission, onCreateMission }: MissionCardsProps) {
  // ALL HOOKS FIRST - React Rules of Hooks compliance
  const [editingMissionId, setEditingMissionId] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [completingMissionId, setCompletingMissionId] = useState<string | null>(null);
  const [showMissionCreationModal, setShowMissionCreationModal] = useState(false);
  // Track previous completion state to detect automatic completions
  const prevCompletionRef = useRef<Record<string, boolean>>({});

  useEffect(() => {
    // Keep previous states in sync and detect auto-completions
    if (activeTab !== 'active') {
      const map: Record<string, boolean> = {};
      missions.forEach(m => {
        map[m.id] = calculateMissionProgress(m, allTodos).meetsRequirements;
      });
      prevCompletionRef.current = map;
      return;
    }

    // Detect transitions: not completed -> meets requirements (without clicking Complete)
    for (const m of missions) {
      const updated = calculateMissionProgress(m, allTodos);
      const prev = prevCompletionRef.current[m.id];
      if (prev !== undefined && updated.meetsRequirements && !prev && completingMissionId !== m.id && !m.isCompleted) {
        setCompletingMissionId(m.id);
        console.log('[MissionCards] auto mission completion animation', m.id);
    setTimeout(() => {
      // Award completion bonus if mission hasn't been completed yet
      if (!m.isCompleted) {
        onCompleteMission(m.id);
      }
      setTimeout(() => {
        setCompletingMissionId((cid) => (cid === m.id ? null : cid));
      }, 250);
    }, 5000);
        break; // handle one at a time
      }
    }

    // Update previous map after checking
    const map: Record<string, boolean> = {};
    missions.forEach(m => {
      map[m.id] = calculateMissionProgress(m, allTodos).meetsRequirements;
    });
    prevCompletionRef.current = map;
  }, [missions, allTodos, activeTab]);

  // Filter missions based on active tab
  const filteredMissions = activeTab === 'active'
    ? missions.filter(m => !m.isCompleted)
    : missions.filter(m => m.isCompleted && completingMissionId !== m.id);

  const handleEditStart = (mission: Mission) => {
    setEditingMissionId(mission.id);
    setEditTitle(mission.name);
  };

  const handleEditSave = (missionId: string) => {
    if (editTitle.trim()) {
      onEditMission(missionId, editTitle.trim());
      setEditingMissionId(null);
      setEditTitle('');
    }
  };

  const handleEditCancel = () => {
    setEditingMissionId(null);
    setEditTitle('');
  };

  const handleMissionComplete = (missionId: string) => {
    setCompletingMissionId(missionId);
    console.log('[MissionCards] start completion animation', missionId);

    // Complete mission after animation (5s), then keep visible briefly for exit animation
    setTimeout(() => {
      console.log('[MissionCards] completing mission', missionId);
      onCompleteMission(missionId);
      setTimeout(() => {
        console.log('[MissionCards] clearing completingMissionId', missionId);
        setCompletingMissionId(null);
      }, 250); // allow exit animation to play
    }, 5000);
  };

  const handleCreateMission = (name: string, size: MissionSize) => {
    onCreateMission(name, size);
    setShowMissionCreationModal(false);
  };

  // Calculate mission completion details
  const getMissionCompletionDetails = (missionId: string) => {
    const mission = missions.find(m => m.id === missionId);
    if (!mission) return null;

    const missionTodos = allTodos.filter(todo => todo.missionId === missionId && todo.completed);
    const totalPoints = missionTodos.length * 10; // Flat 10 points per completed task
    
    return {
      name: mission.name,
      isGeneral: mission.isGeneral,
      completedCount: missionTodos.length,
      totalPoints
    };
  };

  return (
    <Card className="h-full flex flex-col relative">
      <CardContent className="space-y-2 flex-1 overflow-y-auto p-3">
        {filteredMissions.length === 0 ? (
          <div className="flex flex-col items-center justify-center p-6 text-center">
            <div className="text-muted-foreground space-y-3">
              <Target className="w-8 h-8 mx-auto mb-2" />
              <h3 className="font-medium">No missions</h3>
              <Button 
                variant="secondary" 
                size="sm" 
                onClick={() => setShowMissionCreationModal(true)}
                className="mt-2"
              >
                Add Mission
              </Button>
            </div>
          </div>
        ) : filteredMissions.length === 1 ? (
          // Single mission - show circular progress
          (() => {
            const mission = filteredMissions[0];
            const updatedMission = calculateMissionProgress(mission, allTodos);
            const progressPercent = (updatedMission.completedTodos / updatedMission.requiredTodos) * 100;
            const canComplete = updatedMission.meetsRequirements && !mission.isCompleted;

            return (
              <motion.div 
                className="flex flex-col items-center justify-center space-y-4 h-full"
                animate={completingMissionId === mission.id ? { scale: [1, 1.02, 1] } : {}}
                transition={{ duration: 0.3 }}
              >
                {editingMissionId === mission.id ? (
                  <Input
                    value={editTitle}
                    onChange={(e) => setEditTitle(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleEditSave(mission.id);
                      } else if (e.key === 'Escape') {
                        handleEditCancel();
                      }
                    }}
                    onBlur={() => handleEditSave(mission.id)}
                    className="h-8 text-sm font-medium px-3 text-center max-w-[200px]"
                    autoFocus
                  />
                ) : (
                  <CircularProgress
                    value={progressPercent}
                    completedTodos={updatedMission.completedTodos}
                    requiredTodos={updatedMission.requiredTodos}
                    missionName={mission.name}
                  />
                )}
                
                {(canComplete && activeTab === 'active' || !mission.isGeneral) && (
                  <div className="flex items-center gap-2 px-6 pb-6">
                    {canComplete && activeTab === 'active' && (
                      <Button 
                        size="sm"
                        onClick={() => handleMissionComplete(mission.id)}
                        disabled={completingMissionId === mission.id}
                      >
                        Complete Mission
                      </Button>
                    )}
                    {!mission.isGeneral && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleEditStart(mission)}>
                            <Edit2 className="mr-2 h-4 w-4" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            onClick={() => onDeleteMission(mission.id)}
                            className="text-destructive focus:text-destructive"
                          >
                            <Trash2 className="mr-2 h-4 w-4" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </div>
                )}
              </motion.div>
            );
          })()
        ) : (
          // Multiple missions - show list with linear progress
          <AnimatePresence initial={false}>
          {filteredMissions.map(mission => {
            const updatedMission = calculateMissionProgress(mission, allTodos);
            const progressPercent = (updatedMission.completedTodos / updatedMission.requiredTodos) * 100;
            const canComplete = updatedMission.meetsRequirements && !mission.isCompleted;

            return (
              <motion.div 
                key={mission.id} 
                className="relative py-2"
                animate={completingMissionId === mission.id ? { scale: [1, 1.02, 1] } : {}}
                exit={{ opacity: 0, y: 8 }}
                transition={{ duration: 0.3 }}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {editingMissionId === mission.id ? (
                      <Input
                        value={editTitle}
                        onChange={(e) => setEditTitle(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            handleEditSave(mission.id);
                          } else if (e.key === 'Escape') {
                            handleEditCancel();
                          }
                        }}
                        onBlur={() => handleEditSave(mission.id)}
                        className="h-6 text-sm font-medium px-2 py-0"
                        autoFocus
                      />
                    ) : (
                      <h3 className="text-sm font-medium">{mission.name}</h3>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {canComplete && activeTab === 'active' && (
                      <Button 
                        size="sm"
                        onClick={() => handleMissionComplete(mission.id)}
                        disabled={completingMissionId === mission.id}
                        className="h-4 px-1.5 text-xs"
                      >
                        Complete
                      </Button>
                    )}
                    {!mission.isGeneral && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0">
                            <MoreHorizontal className="h-3 w-3" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleEditStart(mission)}>
                            <Edit2 className="mr-2 h-4 w-4" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            onClick={() => onDeleteMission(mission.id)}
                            className="text-destructive focus:text-destructive"
                          >
                            <Trash2 className="mr-2 h-4 w-4" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </div>
                </div>
                <Progress value={progressPercent} className="h-2" />
              </motion.div>
            );
          })}
          </AnimatePresence>
        )}
      </CardContent>

      {/* Container-level Mission Complete Animation */}
      <AnimatePresence>
        {completingMissionId && (
          <motion.div
            className="absolute inset-0 z-50 flex flex-col items-center justify-center pointer-events-none overflow-hidden rounded-lg border bg-card"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <motion.div 
              className="text-center space-y-2"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.4 }}
            >
              {(() => {
                const details = getMissionCompletionDetails(completingMissionId);
                if (!details) return null;
                
                return (
                  <>
                    <h2 className="text-xs font-bold uppercase text-white">MISSION COMPLETE</h2>
                    <p className="text-xl font-medium text-white">
                      {details.isGeneral ? `ROUND ${generalMissionCount + 1}` : details.name}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {details.completedCount} tasks
                    </p>
                    <p className="text-sm text-green-500">
                      +{details.totalPoints} points earned
                    </p>
                  </>
                );
              })()}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <MissionCreationModal
        isOpen={showMissionCreationModal}
        onClose={() => setShowMissionCreationModal(false)}
        onCreateMission={handleCreateMission}
      />
    </Card>
  );
}